import { useState, useEffect, useCallback, useRef } from "react";
import config from "../config";
import { useDataCache, CACHE_KEYS } from "../contexts/DataCacheContext";

// get protection settings from config
const CACHING_ENABLED = config.enableCaching || false;
const CACHE_DURATION = config.protection?.cacheDuration || 5 * 60 * 1000;
const MIN_REFRESH_INTERVAL = config.protection?.minRefreshInterval || 3000;

/**
 * Hook for managing listings with nested requests data.
 * Optimized for the allocation workflow - shows listings with their incoming requests.
 */
export const useAllocateListingsData = (showAllListings = false) => {
  const [loading, setLoading] = useState(true);
  const [listingsData, setListingsData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null); // track when data was last fetched

  // app-level cache context for persistence across page navigations
  const { getCachedData, setCachedData } = useDataCache();

  // cache and rate limiting refs
  const lastFetchTime = useRef(0);
  const lastRefreshTime = useRef(0);
  const cachedData = useRef(null);
  const isFetching = useRef(false);

  // calculate counters from listings data
  const counters = {
    listings: listingsData.length,
    pendingRequests: listingsData.reduce(
      (sum, listing) => sum + (listing.pending_count || 0),
      0
    ),
    totalRequests: listingsData.reduce(
      (sum, listing) => sum + (listing.total_request_count || 0),
      0
    ),
    listingsWithRequests: listingsData.filter(
      (listing) => listing.pending_count > 0
    ).length,
  };

  // fetch listings with requests from API
  const fetchListingsWithRequests = useCallback(
    async (forceRefresh = false) => {
      const now = Date.now();

      // prevent concurrent fetches
      if (isFetching.current) {
        return;
      }

      // use cached data (only if caching enabled)
      if (
        CACHING_ENABLED &&
        !forceRefresh &&
        cachedData.current &&
        now - lastFetchTime.current < CACHE_DURATION
      ) {
        console.log(
          "Using cached listings data (age: " +
            Math.round((now - lastFetchTime.current) / 1000) +
            "s)"
        );
        setListingsData(cachedData.current);
        setErrorMessage("");
        setLastUpdated(lastFetchTime.current); // use cached timestamp
        return;
      }

      isFetching.current = true;

      try {
        console.log(
          CACHING_ENABLED
            ? "Fetching fresh listings data from API (caching enabled)..."
            : "Fetching listings data from API (real-time mode)..."
        );

        const url = `${config.apiUrl}/allocate/listings-with-requests${
          showAllListings ? "?show_all=true" : ""
        }`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok) {
          const listings = data.listings || [];

          cachedData.current = listings;
          lastFetchTime.current = Date.now();
          const timestamp = Date.now();
          setLastUpdated(timestamp);

          // Store in context cache for persistence across page navigations
          setCachedData(CACHE_KEYS.ALLOCATE_LISTINGS, listings, timestamp);

          setListingsData(listings);
          setErrorMessage("");
          console.log(
            `Fetched ${listings.length} listings with requests` +
              (CACHING_ENABLED ? " and cached" : "")
          );
        } else {
          console.error("Failed to fetch listings:", data.message);
          setErrorMessage(data.message || "Failed to load listings.");

          if (response.status === 401) {
            console.error("Unauthorized - token may be expired");
          } else if (response.status >= 500) {
            setErrorMessage("Server error. Please try again later.");
          }
        }
      } catch (error) {
        console.error("Network error fetching listings:", error);
        setErrorMessage(
          "Unable to connect to the server. Please check your connection."
        );
      } finally {
        isFetching.current = false;
      }
    },
    [showAllListings, setCachedData]
  );

  // load data on mount - check context cache first
  useEffect(() => {
    const loadData = async () => {
      // Check context cache first (persists across page navigations)
      const contextCache = getCachedData(CACHE_KEYS.ALLOCATE_LISTINGS);
      if (contextCache && contextCache.data) {
        console.log("Using context-cached data for AllocateListings");
        setListingsData(contextCache.data);
        setLastUpdated(contextCache.timestamp);
        setLoading(false);
        return;
      }

      // No context cache, fetch from API
      await fetchListingsWithRequests(false);
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // refresh data from API with rate limiting
  const performRefresh = useCallback(async () => {
    const now = Date.now();

    if (now - lastRefreshTime.current < MIN_REFRESH_INTERVAL) {
      const waitTime = Math.ceil(
        (MIN_REFRESH_INTERVAL - (now - lastRefreshTime.current)) / 1000
      );
      console.warn(
        `Refresh blocked: please wait ${waitTime}s before refreshing again`
      );
      setErrorMessage(
        `Please wait ${waitTime} second${
          waitTime > 1 ? "s" : ""
        } before refreshing again.`
      );
      return false;
    }

    lastRefreshTime.current = now;
    setErrorMessage("");

    await fetchListingsWithRequests(true);
    return true;
  }, [fetchListingsWithRequests]);

  // allocate material to a request with client-side validation
  const handleAllocate = useCallback(
    async (requestId, quantity) => {
      // Basic client-side validation
      if (!quantity || quantity <= 0) {
        return {
          success: false,
          message: "Please enter a valid quantity",
        };
      }

      // Find the request to get min/max bounds
      let request = null;
      let listing = null;
      for (const l of listingsData) {
        const found = l.requests?.find(r => r.id === requestId);
        if (found) {
          request = found;
          listing = l;
          break;
        }
      }

      // Validate quantity bounds if we found the request
      if (request && listing) {
        if (quantity < request.min_qty) {
          return {
            success: false,
            message: `Quantity must be at least ${request.min_qty} lbs (receiver's minimum)`,
          };
        }
        if (quantity > request.max_qty) {
          return {
            success: false,
            message: `Quantity cannot exceed ${request.max_qty} lbs (receiver's maximum)`,
          };
        }
        if (quantity > listing.remaining_qty) {
          return {
            success: false,
            message: `Cannot allocate ${quantity} lbs. Only ${listing.remaining_qty} lbs remaining.`,
          };
        }
        // Check if already allocated
        if (request.date_allocated) {
          return {
            success: false,
            message: "This request has already been allocated",
          };
        }
        // Check if declined
        if (!request.active) {
          return {
            success: false,
            message: "This request has been declined and cannot be allocated",
          };
        }
      }

      try {
        const url = `${config.apiUrl}/allocate/requests/${requestId}/allocate${
          showAllListings ? "?show_all=true" : ""
        }`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({ quantity }),
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // update local state to reflect allocation
          setListingsData((prevListings) =>
            prevListings.map((listing) => ({
              ...listing,
              // update remaining_qty if this listing contains the allocated request
              remaining_qty:
                listing.requests.some((r) => r.id === requestId)
                  ? data.new_remaining_qty ?? listing.remaining_qty
                  : listing.remaining_qty,
              // update pending_count
              pending_count: listing.requests.some((r) => r.id === requestId)
                ? Math.max(0, listing.pending_count - 1)
                : listing.pending_count,
              // update the request within the listing
              requests: listing.requests.map((req) =>
                req.id === requestId
                  ? {
                      ...req,
                      quantity: quantity,
                      allocated_cost: data.allocated_cost,
                      date_allocated: new Date().toISOString(),
                    }
                  : req
              ),
            }))
          );

          return {
            success: true,
            message: data.message || "Material allocated successfully",
          };
        } else {
          return {
            success: false,
            message: data.message || "Failed to allocate material",
          };
        }
      } catch (error) {
        console.error("Error allocating material:", error);
        return {
          success: false,
          message: "Unable to connect to the server",
        };
      }
    },
    [showAllListings, listingsData]
  );

  // decline a request using the new validated endpoint
  const handleDecline = useCallback(
    async (requestId, reason = null) => {
      try {
        const url = `${config.apiUrl}/allocate/requests/${requestId}/decline${
          showAllListings ? "?show_all=true" : ""
        }`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({ reason }),
          credentials: "include",
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // update local state to reflect decline
          setListingsData((prevListings) =>
            prevListings.map((listing) => ({
              ...listing,
              // update pending_count
              pending_count: listing.requests.some(
                (r) => r.id === requestId && r.active && !r.date_allocated
              )
                ? Math.max(0, listing.pending_count - 1)
                : listing.pending_count,
              // update the request within the listing
              requests: listing.requests.map((req) =>
                req.id === requestId
                  ? {
                      ...req,
                      active: 0,
                    }
                  : req
              ),
            }))
          );

          return {
            success: true,
            message: data.message || "Request declined successfully",
          };
        } else {
          return {
            success: false,
            message: data.message || "Failed to decline request",
          };
        }
      } catch (error) {
        console.error("Error declining request:", error);
        return {
          success: false,
          message: "Unable to connect to the server",
        };
      }
    },
    [showAllListings]
  );

  return {
    loading,
    counters,
    listingsData,
    errorMessage,
    lastUpdated,
    setListingsData,
    setErrorMessage,
    performRefresh,
    handleAllocate,
    handleDecline,
  };
};